import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import Container from "@cloudscape-design/components/container";
import Header from "@cloudscape-design/components/header";
import SpaceBetween from "@cloudscape-design/components/space-between";
import Button from "@cloudscape-design/components/button";
import StatusIndicator from "@cloudscape-design/components/status-indicator";
import Badge from "@cloudscape-design/components/badge";
import Box from "@cloudscape-design/components/box";
import Tabs from "@cloudscape-design/components/tabs";
import Select, { type SelectProps } from "@cloudscape-design/components/select";
import Textarea from "@cloudscape-design/components/textarea";
import Input from "@cloudscape-design/components/input";
import Modal from "@cloudscape-design/components/modal";
import FormField from "@cloudscape-design/components/form-field";
import Flashbar, { type FlashbarProps } from "@cloudscape-design/components/flashbar";
import ColumnLayout from "@cloudscape-design/components/column-layout";
import {
  fetchTicketDetail,
  updateTicketState,
  postComment,
  closeTicket,
  generateDraft,
  getResponseTypeOptions,
  getDecisionLog,
  refineDraft,
} from "../api";
import { getTriageNotes, addTriageNote } from "../api/notes";
import type { WaitingOn, WorkflowStage, ResponseType, DecisionLogEntry, TriageNote, CorrespondenceEntry } from "../types";

const WAITING_ON_OPTIONS: SelectProps.Option[] = [
  { value: "none", label: "None" },
  { value: "reporter", label: "Reporter" },
  { value: "legal", label: "Legal" },
  { value: "eng", label: "Engineering" },
  { value: "sirt", label: "SIRT" },
  { value: "dim", label: "DIM (EU Privacy)" },
];

const STAGE_OPTIONS: SelectProps.Option[] = [
  { value: "WP Intake", label: "WP Intake" },
  { value: "WP Triage", label: "WP Triage" },
  { value: "WP Assessment", label: "WP Assessment" },
  { value: "WP Remediation", label: "WP Remediation" },
  { value: "WP Recovery", label: "WP Recovery" },
  { value: "Post-Incident", label: "Post-Incident" },
  { value: "Resolved", label: "Resolved" },
];

export default function TicketDetailPage() {
  const { ticketId } = useParams<{ ticketId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [notifications, setNotifications] = useState<FlashbarProps.MessageDefinition[]>([]);
  const [showDraftModal, setShowDraftModal] = useState(false);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [draftType, setDraftType] = useState<ResponseType>("FIRST_CONTACT");
  const [draftText, setDraftText] = useState("");
  const [commentText, setCommentText] = useState("");
  const [closeResolution, setCloseResolution] = useState("");
  const [noteText, setNoteText] = useState("");
  const [refineText, setRefineText] = useState("");

  const { data: ticket, isLoading } = useQuery({
    queryKey: ["ticket", ticketId],
    queryFn: () => fetchTicketDetail(ticketId!),
    enabled: !!ticketId,
  });

  const notes = ticketId ? getTriageNotes(ticketId) : [];
  const decisionLog = ticketId ? getDecisionLog({ ticketId }) : [];
  const correspondence: CorrespondenceEntry[] = [];

  function notify(type: "success" | "error", content: string) {
    setNotifications((prev) => [
      ...prev,
      { type, content, dismissible: true, id: String(Date.now()) },
    ]);
  }

  function handleStateChange(field: "waitingOn" | "workflowStage", value: string) {
    if (!ticketId) return;
    updateTicketState(ticketId, { [field]: value });
    queryClient.invalidateQueries({ queryKey: ["ticket", ticketId] });
    queryClient.invalidateQueries({ queryKey: ["tickets"] });
    notify("success", `Updated ${field} to "${value}"`);
  }

  function handleGenerateDraft() {
    if (!ticket) return;
    const result = generateDraft(ticket, draftType);
    setDraftText(result.draft);
    setRefineText("");
  }

  function handleRefineDraft() {
    if (!ticket || !draftText || !refineText.trim()) return;
    const refined = refineDraft(draftText, refineText, ticket);
    setDraftText(refined);
    setRefineText("");
  }

  async function handlePostDraft() {
    if (!ticketId || !draftText) return;
    try {
      await postComment(ticketId, draftText);
      setShowDraftModal(false);
      setDraftText("");
      notify("success", "Comment posted to ticket");
    } catch (err) {
      notify("error", `Failed to post comment: ${err}`);
    }
  }

  async function handlePostComment() {
    if (!ticketId || !commentText) return;
    try {
      await postComment(ticketId, commentText);
      setShowCommentModal(false);
      setCommentText("");
      notify("success", "Comment posted");
    } catch (err) {
      notify("error", `Failed to post comment: ${err}`);
    }
  }

  async function handleCloseTicket() {
    if (!ticketId || !closeResolution) return;
    try {
      await closeTicket(ticketId, closeResolution);
      setShowCloseModal(false);
      setCloseResolution("");
      notify("success", "Ticket closed");
      queryClient.invalidateQueries({ queryKey: ["tickets"] });
    } catch (err) {
      notify("error", `Failed to close ticket: ${err}`);
    }
  }

  function handleAddNote() {
    if (!ticketId || !noteText) return;
    addTriageNote(ticketId, noteText);
    setNoteText("");
    notify("success", "Note added");
  }

  if (isLoading || !ticket) {
    return <StatusIndicator type="loading">Loading ticket...</StatusIndicator>;
  }

  return (
    <SpaceBetween size="l">
      <Flashbar items={notifications} />

      <Header
        variant="h1"
        actions={
          <SpaceBetween direction="horizontal" size="xs">
            <Button onClick={() => navigate("/")}>← Back</Button>
            <Button variant="primary" onClick={() => setShowDraftModal(true)}>
              Draft Response
            </Button>
            <Button onClick={() => setShowCommentModal(true)}>Post Comment</Button>
            <Button onClick={() => setShowCloseModal(true)}>Close Ticket</Button>
          </SpaceBetween>
        }
      >
        {ticket.ticketId} — {ticket.title}
      </Header>

      {/* Ticket metadata */}
      <Container>
        <ColumnLayout columns={4} variant="text-grid">
          <SpaceBetween size="xxs">
            <Box variant="awsui-key-label">Severity</Box>
            <StatusIndicator
              type={
                ticket.severity === "SEV_2"
                  ? "error"
                  : ticket.severity === "SEV_3"
                    ? "warning"
                    : "success"
              }
            >
              {ticket.severity.replace("SEV_", "Sev-")}
            </StatusIndicator>
          </SpaceBetween>
          <SpaceBetween size="xxs">
            <Box variant="awsui-key-label">Age</Box>
            <Box>{ticket.daysOpen} days</Box>
          </SpaceBetween>
          <SpaceBetween size="xxs">
            <Box variant="awsui-key-label">Stale</Box>
            <StatusIndicator type={ticket.daysStale > 7 ? "error" : ticket.daysStale > 3 ? "warning" : "success"}>
              {ticket.daysStale} days
            </StatusIndicator>
          </SpaceBetween>
          <SpaceBetween size="xxs">
            <Box variant="awsui-key-label">EU / GDPR</Box>
            <Box>{ticket.isEu ? <Badge color="blue">Yes</Badge> : "No"}</Box>
          </SpaceBetween>
        </ColumnLayout>
      </Container>

      {/* State controls */}
      <Container header={<Header variant="h2">Triage State</Header>}>
        <ColumnLayout columns={3}>
          <FormField label="Waiting On">
            <Select
              selectedOption={
                WAITING_ON_OPTIONS.find((o) => o.value === ticket.waitingOn) || WAITING_ON_OPTIONS[0]
              }
              options={WAITING_ON_OPTIONS}
              onChange={({ detail }) =>
                handleStateChange("waitingOn", detail.selectedOption.value as WaitingOn)
              }
            />
          </FormField>
          <FormField label="Workflow Stage">
            <Select
              selectedOption={
                STAGE_OPTIONS.find((o) => o.value === ticket.workflowStage) || STAGE_OPTIONS[0]
              }
              options={STAGE_OPTIONS}
              onChange={({ detail }) =>
                handleStateChange("workflowStage", detail.selectedOption.value as WorkflowStage)
              }
            />
          </FormField>
          <FormField label="Assignee">
            <Box>{ticket.assignee}</Box>
          </FormField>
        </ColumnLayout>
      </Container>

      {/* Description */}
      <Container header={<Header variant="h2">Description</Header>}>
        <Box variant="p">{ticket.description || "No description available."}</Box>
      </Container>

      {/* Tabs: Correspondence, Notes + Decision Log */}
      <Tabs
        tabs={[
          {
            id: "correspondence",
            label: `Correspondence (${correspondence.length})`,
            content: (
              <SpaceBetween size="s">
                {correspondence.map((entry: CorrespondenceEntry, i: number) => (
                  <Container key={i}>
                    <SpaceBetween size="xxs">
                      <Box variant="small" color="text-body-secondary">
                        {entry.author} — {new Date(entry.timestamp).toLocaleString()}
                      </Box>
                      <Box>{entry.content}</Box>
                    </SpaceBetween>
                  </Container>
                ))}
                {correspondence.length === 0 && (
                  <Box color="text-body-secondary">No correspondence yet.</Box>
                )}
              </SpaceBetween>
            ),
          },
          {
            id: "notes",
            label: `Triage Notes (${notes.length})`,
            content: (
              <SpaceBetween size="m">
                <SpaceBetween size="xs">
                  <Textarea
                    value={noteText}
                    onChange={({ detail }) => setNoteText(detail.value)}
                    placeholder="Add a triage note..."
                    rows={3}
                  />
                  <Button onClick={handleAddNote} disabled={!noteText.trim()}>
                    Add Note
                  </Button>
                </SpaceBetween>
                {notes.map((note: TriageNote) => (
                  <Container key={note.noteId}>
                    <SpaceBetween size="xxs">
                      <Box variant="small" color="text-body-secondary">
                        {note.author} — {new Date(note.createdAt).toLocaleString()}
                      </Box>
                      <Box>{note.content}</Box>
                    </SpaceBetween>
                  </Container>
                ))}
                {notes.length === 0 && (
                  <Box color="text-body-secondary">No triage notes yet.</Box>
                )}
              </SpaceBetween>
            ),
          },
          {
            id: "log",
            label: `Decision Log (${decisionLog.length})`,
            content: (
              <SpaceBetween size="s">
                {decisionLog.map((entry: DecisionLogEntry, i: number) => (
                  <Box key={i}>
                    <Box variant="small" color="text-body-secondary">
                      {new Date(entry.timestamp).toLocaleString()} — {entry.actor} — {entry.actionType}
                    </Box>
                    <Box>{entry.details}</Box>
                  </Box>
                ))}
                {decisionLog.length === 0 && (
                  <Box color="text-body-secondary">No actions recorded yet.</Box>
                )}
              </SpaceBetween>
            ),
          },
        ]}
      />

      {/* Draft Response Modal */}
      <Modal
        visible={showDraftModal}
        onDismiss={() => setShowDraftModal(false)}
        header="Draft Response"
        footer={
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={() => setShowDraftModal(false)}>
              Cancel
            </Button>
            {draftText && (
              <Button variant="primary" onClick={handlePostDraft}>
                Post to Ticket
              </Button>
            )}
          </SpaceBetween>
        }
        size="large"
      >
        <SpaceBetween size="m">
          <FormField label="Response Type">
            <Select
              selectedOption={
                getResponseTypeOptions().find((o) => o.value === draftType) || null
              }
              options={getResponseTypeOptions().map((o) => ({
                value: o.value,
                label: o.label,
                description: o.description,
              }))}
              onChange={({ detail }) =>
                setDraftType(detail.selectedOption.value as ResponseType)
              }
            />
          </FormField>
          <Button onClick={handleGenerateDraft}>Generate Draft</Button>
          {draftText && (
            <>
              <FormField label="Draft (editable)">
                <Textarea
                  value={draftText}
                  onChange={({ detail }) => setDraftText(detail.value)}
                  rows={12}
                />
              </FormField>
              <FormField
                label="Refine"
                description="Give feedback to adjust the draft (e.g., 'make it more urgent', 'add a deadline of July 14', 'remove the last question')"
              >
                <SpaceBetween direction="horizontal" size="xs">
                  <div style={{ flex: 1 }}>
                    <Input
                      value={refineText}
                      onChange={({ detail }) => setRefineText(detail.value)}
                      placeholder="e.g., make it shorter and add urgency..."
                      onKeyDown={({ detail }) => {
                        if (detail.key === "Enter" && refineText.trim()) {
                          handleRefineDraft();
                        }
                      }}
                    />
                  </div>
                  <Button onClick={handleRefineDraft} disabled={!refineText.trim()}>
                    Refine
                  </Button>
                </SpaceBetween>
              </FormField>
            </>
          )}
        </SpaceBetween>
      </Modal>

      {/* Post Comment Modal */}
      <Modal
        visible={showCommentModal}
        onDismiss={() => setShowCommentModal(false)}
        header="Post Comment"
        footer={
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={() => setShowCommentModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handlePostComment} disabled={!commentText.trim()}>
              Post
            </Button>
          </SpaceBetween>
        }
      >
        <Textarea
          value={commentText}
          onChange={({ detail }) => setCommentText(detail.value)}
          placeholder="Write your comment..."
          rows={6}
        />
      </Modal>

      {/* Close Ticket Modal */}
      <Modal
        visible={showCloseModal}
        onDismiss={() => setShowCloseModal(false)}
        header="Close Ticket"
        footer={
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={() => setShowCloseModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleCloseTicket} disabled={!closeResolution.trim()}>
              Confirm Close
            </Button>
          </SpaceBetween>
        }
      >
        <FormField label="Resolution" description="Explain why this ticket is being closed.">
          <Textarea
            value={closeResolution}
            onChange={({ detail }) => setCloseResolution(detail.value)}
            placeholder="Incident remediated. Access revoked and employee notified."
            rows={4}
          />
        </FormField>
      </Modal>
    </SpaceBetween>
  );
}
